/**
 * Teacher Management API Service
 * Handles all API calls for teacher management features
 */

import { apiService } from '@shared/services/api/ApiService';
import type {
  Student,
  StudentDetail,
  StudentFilters,
  Class,
  ClassMember,
  CreateClassRequest,
  UpdateClassRequest,
  PaginatedResponse
} from '@shared/types/teach';

const API_BASE = '/teach';

export const teachApi = {
  // ============= Student APIs =============
  
  /**
   * Get list of students referred by the teacher
   */
  async getStudents(filters?: StudentFilters): Promise<PaginatedResponse<Student>> {
    const response = await apiService.get(`${API_BASE}/students`, { params: filters });
    // ApiService.get() returns the response.data directly, which is { success, data, pagination }
    return {
      data: response.data || [],
      pagination: (response as any).pagination || { total: 0, page: 1, pageSize: 20, totalPages: 0 }
    };
  },

  /**
   * Get detailed information for a specific student
   */
  async getStudentDetail(studentId: string): Promise<StudentDetail> {
    const response = await apiService.get(`${API_BASE}/students/${studentId}`);
    return response.data;
  },

  // ============= Class APIs =============
  
  /**
   * Get all classes created by the teacher
   */
  async getClasses(): Promise<{ classes: Class[] }> {
    const response = await apiService.get(`${API_BASE}/classes`);
    // ApiService.get() returns { success, data: classes[] }
    return { classes: response.data || [] };
  },

  /**
   * Create a new class
   */
  async createClass(data: CreateClassRequest): Promise<{ class: Class }> {
    const response = await apiService.post(`${API_BASE}/classes`, data);
    // ApiService.post() returns { success, data: newClass }
    return { class: response.data };
  },

  /**
   * Update class information
   */
  async updateClass(classId: string, data: UpdateClassRequest): Promise<void> {
    await apiService.put(`${API_BASE}/classes/${classId}`, data);
  },

  /**
   * Delete class (soft delete)
   */
  async deleteClass(classId: string): Promise<void> {
    await apiService.delete(`${API_BASE}/classes/${classId}`);
  },

  // ============= Class Member APIs =============
  
  /**
   * Get all members of a class
   */
  async getClassMembers(classId: string): Promise<{ members: ClassMember[] }> {
    const response = await apiService.get(`${API_BASE}/classes/${classId}/members`);
    // ApiService.get() returns { success, data: members[] }
    return { members: response.data || [] };
  },

  /**
   * Add members to class (batch operation)
   */
  async addMembersToClass(classId: string, studentIds: string[]): Promise<void> {
    await apiService.post(`${API_BASE}/classes/${classId}/members`, { studentIds });
  },

  /**
   * Remove member from class
   */
  async removeMemberFromClass(classId: string, studentId: string): Promise<void> {
    await apiService.delete(`${API_BASE}/classes/${classId}/members/${studentId}`);
  },
};
